import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, Info, ArrowRight } from 'lucide-react';
import Modal from '../ui/Modal';
import { useAiCredits } from '../../contexts/AiCreditsContext';
import { estimativaDe, saldoInsuficiente, reais, creditos, AiCreditSummary } from '../../services/aiCreditsService';

interface Props {
  /** Funcionalidade como fica no extrato (rota ou fila): social, ai-generation, image-studio... */
  feature: string;
  /** Ação dentro da funcionalidade: generate-caption, landing-page-chat... */
  action?: string | null;
  /** Quantas vezes a ação roda neste clique (ex.: 4 imagens) */
  quantity?: number;
  /** Rótulo da unidade quando não é "por uso" (ex.: "por execução") */
  unitLabel?: string;
  /** Classes do contêiner (alinhamento e margem de cada tela) */
  className?: string;
  /** "dark" para telas de fundo escuro fixo (editor de landing page, estúdio de vídeo) */
  tone?: 'auto' | 'dark';
}

/** Custo estimado da ação e saldo da conta (compartilhado pelo selo e por quem bloqueia o botão). */
export const useAiCost = (feature: string, action: string | null = null, quantity = 1) => {
  const { summary, estimates } = useAiCredits();
  const est = estimativaDe(estimates, feature, action);
  const custo = est.cents * Math.max(1, quantity);
  return { estimate: est, costCents: custo, insufficient: saldoInsuficiente(summary, custo), summary };
};

const Bloco: React.FC<{ rotulo: string; valor: string; extra?: React.ReactNode; destaque?: boolean }> = ({ rotulo, valor, extra, destaque }) => (
  <div className={`rounded-xl border p-3 ${destaque ? 'border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60'}`}>
    <p className="text-xs text-gray-500 dark:text-gray-400">{rotulo}</p>
    <p className={`text-base font-semibold tabular-nums ${destaque ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>{valor}</p>
    {extra}
  </div>
);

const Saldo: React.FC<{ summary: AiCreditSummary }> = ({ summary }) => {
  const { t } = useTranslation();
  if (summary.postpaid) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Bloco destaque rotulo={t('aiCredits.spentMonth', 'Gasto no mês')} valor={reais(summary.month_spent_cents)} />
        <Bloco rotulo={t('aiCredits.billing', 'Cobrança')} valor={t('aiCredits.postpaid', 'Pós-pago')} />
      </div>
    );
  }
  const total = Math.max(0, summary.monthly_allowance_cents);
  const resta = Math.max(0, summary.included_remaining_cents);
  const pct = total > 0 ? Math.min(100, Math.round((resta / total) * 100)) : 0;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <Bloco destaque rotulo={t('aiCredits.available', 'Disponível')} valor={reais(summary.available_cents)} />
      <Bloco
        rotulo={t('aiCredits.planMonth', 'Do plano no mês')}
        valor={`${reais(resta)}`}
        extra={
          <>
            <div className="mt-1.5 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden" aria-hidden="true">
              <div className="h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{t('aiCredits.ofTotal', 'de {{total}}', { total: reais(total) })}</p>
          </>
        }
      />
      <Bloco rotulo={t('aiCredits.purchased', 'Comprado')} valor={reais(summary.purchased_balance_cents)} extra={<p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{t('aiCredits.neverExpires', 'não vence')}</p>} />
    </div>
  );
};

/**
 * Selo de custo ao lado de cada ação de IA: um botão pequeno com o custo médio da ação, que abre
 * uma janela com o detalhe (custo, saldo do mês, quando a cobrança começa) e a recarga. Saldo que
 * não cobre a ação deixa o selo vermelho antes do clique.
 */
const AiCostBadge: React.FC<Props> = ({ feature, action = null, quantity = 1, unitLabel, className = '', tone = 'auto' }) => {
  const { t, i18n } = useTranslation();
  const { openRecharge } = useAiCredits();
  const { estimate, costCents, insufficient, summary } = useAiCost(feature, action, quantity);
  const [aberto, setAberto] = useState(false);

  const unidade = unitLabel || (quantity > 1 ? t('aiCredits.perClick', 'nesta geração') : t('aiCredits.perUse', 'por uso'));
  const n = creditos(costCents);
  const origem = estimate.source === 'account'
    ? t('aiCredits.sourceAccount', 'Média dos usos desta conta nos últimos 30 dias.')
    : estimate.source === 'global' || estimate.source === 'feature'
      ? t('aiCredits.sourceGlobal', 'Média de todos os clientes nos últimos 30 dias.')
      : t('aiCredits.sourceEstimate', 'Estimativa: ainda não há usos suficientes para calcular a média.');
  const inicio = summary ? new Date(summary.billing_start).toLocaleDateString(i18n.language || 'pt-BR') : '';

  const recarregar = () => { setAberto(false); openRecharge(); };

  return (
    // A janela abre num portal, mas os cliques sobem pela árvore do React: não chegam ao card de fora
    <div className={`flex ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setAberto(true)}
        title={t('aiCredits.badgeTitle', 'Custo de IA desta ação: clique para ver o detalhe e o saldo') as string}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap transition-colors ${insufficient
          ? (tone === 'dark' ? 'border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20' : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300')
          : tone === 'dark'
            ? 'border-white/10 bg-white/5 text-gray-400 hover:border-indigo-400/50 hover:text-indigo-200'
            : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-primary-700 dark:hover:text-primary-300'}`}
      >
        {insufficient ? <AlertTriangle className="h-3 w-3" /> : <Sparkles className={`h-3 w-3 ${tone === 'dark' ? 'text-indigo-400' : 'text-primary-500'}`} />}
        <span className="tabular-nums">{t('aiCredits.badge', '~{{n}} créditos', { n })}</span>
        {/* Unidade informada pela tela (por imagem, por rodada...) separa selos lado a lado */}
        {(unitLabel || quantity > 1) && <span className="font-normal opacity-75">{unidade}</span>}
        <Info className="h-3 w-3 opacity-50" aria-hidden="true" />
      </button>

      <Modal isOpen={aberto} onClose={() => setAberto(false)} title={t('aiCredits.modalTitle', 'Custo de IA') as string} size="md">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-xl bg-gradient-to-br from-primary-500/15 to-purple-500/15 p-2.5">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                {t('aiCredits.badge', '~{{n}} créditos', { n })}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('aiCredits.approx', '≈ {{valor}} {{unidade}}', { valor: reais(costCents), unidade })}
                {estimate.tokens ? ` · ~${creditos(estimate.tokens * Math.max(1, quantity))} tokens` : ''}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{origem}</p>
            </div>
          </div>

          {insufficient && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('aiCredits.insufficientLong', 'O saldo não cobre esta ação. Recarregue para continuar: o que você está fazendo não se perde.')}</span>
            </div>
          )}

          {summary && <Saldo summary={summary} />}

          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3 text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <p>{t('aiCredits.creditValue', '1 crédito = R$ 0,01. O gasto sai primeiro do plano do mês, que renova no dia 1º, e depois do que foi comprado.')}</p>
            {summary && !summary.billing_active && (
              <p className="font-medium text-gray-700 dark:text-gray-300">{t('aiCredits.notChargedYet', 'Até {{data}} nada é descontado: o consumo só fica registrado.', { data: inicio })}</p>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
            <Link to="/settings/ai-usage" onClick={() => setAberto(false)} className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
              {t('aiCredits.viewUsage', 'Ver consumo detalhado')} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            {!summary?.postpaid && (
              <button type="button" onClick={recarregar} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
                <Sparkles className="h-4 w-4" /> {t('aiCredits.recharge', 'Recarregar')}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AiCostBadge;
